import { useCallback, useState } from 'react';
import * as zod from 'zod';
import { voyageSuggestionParamsSchema } from '../core/suggestions/VoyageSuggestionParams';
import { FilterStateSchema } from '../ui/QueryBuilder/lib';

const columnSortBySchema = zod.object({
  id: zod.string(),
  desc: zod.boolean().optional(),
});

const settingPropertiesSchema = zod.object({
  compressedView: zod.boolean().optional(),
  hideIncompatibleContracts: zod.boolean().optional(),
  hideEmptyTimelines: zod.boolean().optional(),

  timelineKeyword: zod.string().optional(),

  voyageKeyword: zod.string().optional(),
  voyagesSearchKeywords: zod.array(zod.string()).optional(),
  voyagesTableHiddenColumns: zod.array(zod.string()).optional(),
  voyagesTableSortBy: zod.array(columnSortBySchema).optional(),

  orderKeyword: zod.string().optional(),
  ordersTableHiddenColumns: zod.array(zod.string()).optional(),
  ordersTableSortBy: zod.array(columnSortBySchema).optional(),
  contractsTableSortBy: zod.array(columnSortBySchema).optional(),
  contractsFilterSearch: zod.string().optional(),

  planningOrdersTableHiddenColumns: zod.array(zod.string()).optional(),
  planningOrdersTableSortBy: zod.array(columnSortBySchema).optional(),
  planningOrdersTableShowTimes: zod.boolean().optional(),
  planningOrdersTableGroupByWeek: zod.boolean().optional(),
  planningOrdersTableJumpWeekMode: zod.enum(['offset', 'week']).optional(),
  planningOrdersTableJumpWeekOffset: zod.number().optional(),
  planningOrdersTableJumpWeekTarget: zod.number().optional(),
  planningOrdersFilterState: FilterStateSchema.optional(),
  planningHiddenOrderTabs: zod.array(zod.string()).optional(),
  planningTimelinesFilterSearch: zod.string().optional(),

  trashBinOrdersFilterSearch: zod.string().optional(),
  trashBinTableShowTimes: zod.boolean().optional(),
  trashBinTableHiddenColumns: zod.array(zod.string()).optional(),

  requestLogFilterSearch: zod.string().optional(),

  incomingsFilterSearch: zod.string().optional(),
  incomingTableSortBy: zod.array(columnSortBySchema).optional(),

  scenarioCIDSaved: zod.string().nullable().optional(),

  commitPolicy: zod.enum(['merge', 'replace']).optional(),

  commitVesselScope: zod
    .enum(['all', 'shortlist', 'shown', 'selected'])
    .optional(),

  vesselDateRange: zod
    .object({
      from: zod.string().optional(),
      to: zod.string().optional(),
    })
    .optional(),

  scenarioComparisonAutomaticFiltersEnabled: zod.boolean().optional(),
  scenarioComparisonSelectedScenarios: zod.array(zod.string()).optional(),
  scenarioComparisonDateFilter: zod
    .object({
      start: zod.number(),
      end: zod.number().nullable(),
    })
    .optional(),
  scenarioComparisonHiddenRows: zod.array(zod.string()).optional(),
  scenarioComparisonVesselFilter: zod
    .array(
      zod.object({
        type: zod.enum(['vessel', 'attribute']),
        id: zod.number(),
      }),
    )
    .optional(),

  suggestVoyageModalConfig: voyageSuggestionParamsSchema.optional(),
  suggestVoyageModalConfig_userDefaults: voyageSuggestionParamsSchema
    .nullable()
    .optional(),
});

type SettingProperties = zod.infer<typeof settingPropertiesSchema>;

export type LocalConfig = {
  getSetting: <T extends keyof SettingProperties>(
    key: T,
  ) => SettingProperties[T] | undefined;
  setSetting: <T extends keyof SettingProperties>(
    key: T,
    value: SettingProperties[T],
  ) => void;
};

const STORAGE_KEY = 'localConfigSettings';

const readSettings = (): SettingProperties => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const storedSettings = JSON.parse(localStorage[STORAGE_KEY] ?? '{}');

    const [validatedSettings, invalidKeys] = Object.entries(
      settingPropertiesSchema.shape,
    ).reduce<[Partial<SettingProperties>, string[]]>(
      ([validated, invalid], [key, schema]) => {
        if (key in storedSettings) {
          try {
            const validatedRecord = validated as Record<string, unknown>;
            validatedRecord[key] = schema.parse(storedSettings[key]);
          } catch (err) {
            console.error(`Validation failed for property "${key}":`, err);
            invalid.push(key);
          }
        }
        return [validated, invalid];
      },
      [{}, []],
    );

    if (invalidKeys.length > 0) {
      invalidKeys.forEach((key) => delete storedSettings[key]);
      localStorage[STORAGE_KEY] = JSON.stringify(storedSettings);
    }

    return { ...storedSettings, ...validatedSettings };
  } catch (err) {
    console.warn(`Failed to parse local configuration:`, err);
    return {};
  }
};

const writeSettings = (settings: Partial<SettingProperties>): void => {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage[STORAGE_KEY] = JSON.stringify(settings);
};

/**
 * Remove a single persisted local setting, preserving all other settings.
 * Used on logout to clear session-scoped keys (e.g. the saved scenario) without
 * discarding unrelated user preferences (table columns, filters, …).
 */
export const clearLocalSetting = (key: keyof SettingProperties): void => {
  const settings = readSettings();
  if (!(key in settings)) {
    return;
  }
  const { [key]: _removed, ...rest } = settings;
  writeSettings(rest);
};

type ValueOrGetter<T> = T | (() => T);

/**
 * Use like useState, except the value is stored in localStorage across sessions.
 * To add a new config property for use just add it to the SettingProperties mapping first.
 */
export function useLocalSetting<T extends keyof SettingProperties>(
  key: T,
  initialValue: Exclude<SettingProperties[T], undefined>,
): [
  value: Exclude<SettingProperties[T], undefined>,
  setValue: (newValue: ValueOrGetter<SettingProperties[T]>) => void,
];
export function useLocalSetting<T extends keyof SettingProperties>(
  key: T,
): [
  value: SettingProperties[T] | undefined,
  setValue: (newValue: ValueOrGetter<SettingProperties[T]>) => void,
];
export function useLocalSetting<T extends keyof SettingProperties>(
  key: T,
  initialValue?: SettingProperties[T],
): [
  value: SettingProperties[T] | undefined,
  setValue: (newValue: ValueOrGetter<SettingProperties[T]>) => void,
] {
  const settings = readSettings();
  const [value, setValueInternal] = useState<SettingProperties[T] | undefined>(
    settings[key] ?? initialValue,
  );

  const setValue = useCallback(
    (newValue: ValueOrGetter<SettingProperties[T]>) => {
      const settings = readSettings();
      settings[key] = typeof newValue === 'function' ? newValue() : newValue;
      writeSettings(settings);
      setValueInternal(newValue);
    },
    [setValueInternal, key],
  );

  return [value, setValue];
}
